import { ChangeEventArgs as DropDownChangeEventArgs, DropDownList } from '@syncfusion/ej2-dropdowns';
import { Column, ColumnChooserEventArgs, Grid } from '@syncfusion/ej2-grids';

type ColumnChooserSortOrder = 'grid' | 'az' | 'za';

type ColumnChooserState = {
  originalHeaders: Record<string, string>;
  chooserNames: Record<string, string>;
  gridOrder: string[];
  sortOrder: ColumnChooserSortOrder;
  observer?: MutationObserver;
  applyingSort?: boolean;
  dropdown?: DropDownList;
};

const columnChooserStates: Record<string, ColumnChooserState> = {};

export type ColumnChooserGetGrid = (gridId: string) => Grid | undefined;

const getColumnChooserState = (gridId: string): ColumnChooserState => {
  if (!columnChooserStates[gridId]) {
    columnChooserStates[gridId] = {
      originalHeaders: {},
      chooserNames: {},
      gridOrder: [],
      sortOrder: 'grid',
    };
  }

  return columnChooserStates[gridId];
};

const getChooserUid = (element: Element): string | undefined => {
  const id = element.id;

  if (id?.startsWith('e-ccli_')) {
    return id.substring('e-ccli_'.length);
  }

  const wrapper = element.querySelector('.e-ccheck');

  return wrapper?.getAttribute('uid') ?? undefined;
};

const destroyColumnChooser = (gridId: string) => {
  const state = columnChooserStates[gridId];

  if (!state) {
    return;
  }

  if (state.dropdown) {
    state.dropdown.destroy();

    const chooser = document.getElementById(`${gridId}_ccdlg`);
    const controls = chooser?.querySelector('.pf-column-chooser-controls');

    if (controls) {
      controls.innerHTML = '';
    }

    state.dropdown = undefined;
  }

  if (state.observer) {
    state.observer.disconnect();
    state.observer = undefined;
  }

  state.originalHeaders = {};
  state.chooserNames = {};
};

const setColumnChooserLabels = (gridId: string) => {
  const state = columnChooserStates[gridId];

  if (!state) {
    return;
  }

  const chooser = document.getElementById(`${gridId}_ccdlg`);

  if (!chooser) {
    return;
  }

  const items = chooser.querySelectorAll<HTMLLIElement>('.e-cclist');

  for (const item of Array.from(items)) {
    if (item.classList.contains('e-cc-selectall')) {
      continue;
    }

    const uid = getChooserUid(item);

    if (!uid) {
      continue;
    }

    const label = item.querySelector<HTMLElement>('.e-label');

    if (label) {
      const chooserName = state.chooserNames[uid] ?? state.originalHeaders[uid] ?? label.textContent ?? '';
      if (chooserName.includes('<') && chooserName.includes('>')) {
        label.innerHTML = chooserName;
      } else {
        label.textContent = chooserName;
      }
    }
  }
};

const applyColumnChooserSort = (gridId: string) => {
  const state = columnChooserStates[gridId];

  if (!state) {
    return;
  }

  const chooser = document.getElementById(`${gridId}_ccdlg`);
  const list = chooser?.querySelector('.e-ccul-ele');

  if (!chooser || !list) {
    return;
  }

  const items = Array.from(list.querySelectorAll('li.e-cclist')).filter(
    (item) => !item.classList.contains('e-cc-selectall'),
  );

  if (items.length === 0) {
    return;
  }

  const sortedItems = [...items];

  const getGridIndex = (uid: string | undefined) => {
    if (!uid) {
      return Number.MAX_SAFE_INTEGER;
    }

    const index = state.gridOrder.indexOf(uid);

    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  };

  const getName = (uid: string | undefined) => {
    if (!uid) {
      return '';
    }

    return (state.chooserNames[uid] ?? '').toLocaleLowerCase();
  };

  if (state.sortOrder === 'grid') {
    sortedItems.sort((a, b) => getGridIndex(getChooserUid(a)) - getGridIndex(getChooserUid(b)));
  } else {
    const direction = state.sortOrder === 'az' ? 1 : -1;

    sortedItems.sort((a, b) => {
      const nameA = getName(getChooserUid(a));
      const nameB = getName(getChooserUid(b));

      if (nameA < nameB) {
        return -1 * direction;
      }

      if (nameA > nameB) {
        return 1 * direction;
      }

      return getGridIndex(getChooserUid(a)) - getGridIndex(getChooserUid(b));
    });
  }

  state.applyingSort = true;

  for (const item of sortedItems) {
    list.appendChild(item);
  }

  setColumnChooserLabels(gridId);

  window.setTimeout(() => {
    state.applyingSort = false;
  }, 0);
};

const applyColumnChooserUI = (gridId: string, getGrid: ColumnChooserGetGrid) => {
  const grid = getGrid(gridId);
  const state = columnChooserStates[gridId];

  if (!grid || !state) {
    return;
  }

  const chooser = document.getElementById(`${gridId}_ccdlg`);

  if (!chooser || chooser.classList.contains('e-popup-close')) {
    return;
  }

  const header = chooser.querySelector('.e-dlg-header-content');

  if (!header) {
    window.setTimeout(() => applyColumnChooserUI(gridId, getGrid), 25);
    return;
  }

  header.classList.add('pf-column-chooser-header');

  let controls = header.querySelector('.pf-column-chooser-controls') as HTMLDivElement | null;

  if (!controls) {
    controls = document.createElement('div');
    controls.className = 'pf-column-chooser-controls';
    header.appendChild(controls);
  }

  if (!state.dropdown) {
    controls.innerHTML = '';

    const host = document.createElement('input');
    host.type = 'text';
    host.className = 'pf-column-chooser-sort-host';
    controls.appendChild(host);

    const dropdown = new DropDownList({
      cssClass: 'pf-column-chooser-sort',
      dataSource: [
        { text: 'Column order', value: 'grid' },
        { text: 'A-Z', value: 'az' },
        { text: 'Z-A', value: 'za' },
      ],
      fields: {
        text: 'text',
        value: 'value',
      },
      width: '140px',
      value: state.sortOrder,
      change: (args: DropDownChangeEventArgs) => {
        const value = typeof args.value === 'string' ? (args.value as ColumnChooserSortOrder) : undefined;
        state.sortOrder = value && ['grid', 'az', 'za'].includes(value) ? value : 'grid';
        applyColumnChooserSort(gridId);
      },
    });

    dropdown.appendTo(host);

    host.setAttribute('aria-label', 'Sort columns');
    host.setAttribute('title', 'Sort column list');

    state.dropdown = dropdown;
  } else if (state.dropdown.value !== state.sortOrder) {
    state.dropdown.value = state.sortOrder;
  }

  if (!state.observer) {
    state.observer = new MutationObserver(() => {
      if (state.applyingSort) {
        return;
      }

      applyColumnChooserSort(gridId);
    });

    const list = chooser.querySelector('.e-ccul-ele');

    if (list) {
      state.observer.observe(list, { childList: true });
    }
  }

  applyColumnChooserSort(gridId);
};

export const createBeforeOpenColumnChooser =
  (getGrid: ColumnChooserGetGrid) => (gridId: string) => (args: ColumnChooserEventArgs) => {
    const grid = getGrid(gridId);

    if (!grid) {
      return;
    }

    destroyColumnChooser(gridId);

    const state = getColumnChooserState(gridId);

    state.originalHeaders = {};
    state.chooserNames = {};

    const gridColumns = (grid.getColumns() as Column[]).filter(
      (column) => column.showInColumnChooser !== false && !!column.uid,
    );

    state.gridOrder = gridColumns.map((column) => column.uid as string);

    const chooserColumns = (args.columns ?? gridColumns) as Column[];

    for (const column of chooserColumns) {
      const uid = column.uid as string | undefined;

      if (!uid) {
        continue;
      }

      state.originalHeaders[uid] = column.headerText ?? '';

      const chooserName = (column as { chooserName?: string }).chooserName ?? column.headerText ?? '';

      state.chooserNames[uid] = chooserName || state.originalHeaders[uid] || '';
    }

    window.setTimeout(() => applyColumnChooserUI(gridId, getGrid), 0);
  };
